/**
 * Cataract-surgery global-surgical-period billing, ophthalmology's own CPT catalog against the
 * **same, reused** date-arithmetic/modifier-selection mechanism in `../globalSurgicalPeriod.ts` —
 * originally built for orthopedics (M7), extracted to that neutral, specialty-agnostic location once
 * ophthalmology became a second real consumer, so this module depends on a shared location rather
 * than reaching into another specialty module's own internals. `BACKLOG.md`'s own cross-cutting note
 * for the M5–M11 batch states this directly: "'is this visit already bundled into a prior episode's
 * fee' is the same underlying billing pattern behind both M7's global surgical period and M11's
 * antepartum-visit-counter — one shared mechanism, not two module-specific ones." This module only
 * adds ophthalmology's own CPT catalog, the one genuinely module-specific piece — and actually calls
 * `computeGlobalPeriodWindow`/`determineGlobalPeriodModifier` (`ophthalmology/routes.ts`'s response
 * shaping and its cataract-surgical-plan modifier-query endpoint), not merely re-exporting them.
 *
 * **UNREVIEWED** — see `../globalSurgicalPeriod.ts`'s own header: the global-period-days value below
 * is carried over from general knowledge (cataract extraction is a well-known 90-day major-surgery
 * global procedure), not checked against the current CMS Physician Fee Schedule Relative Value File
 * during this build.
 */

export {
  computeGlobalPeriodWindow,
  determineGlobalPeriodModifier,
  GLOBAL_PERIOD_MODIFIERS,
  GLOBAL_PERIOD_RELATIONSHIPS,
  globalPeriodRelationshipLabel,
  isGlobalPeriodDays,
  isGlobalPeriodRelationship,
  isWithinGlobalPeriod,
  type GlobalPeriodDays,
  type GlobalPeriodModifier,
  type GlobalPeriodModifierResult,
  type GlobalPeriodRelationship,
  type GlobalPeriodWindow,
} from '../globalSurgicalPeriod.ts';

export interface OphthalmologyCataractCptCode {
  readonly code: string;
  readonly description: string;
  readonly globalPeriodDays: 0 | 10 | 90;
}

/** UNREVIEWED — see this module's own header. */
export const OPHTHALMOLOGY_CATARACT_CPT_CODES: readonly OphthalmologyCataractCptCode[] = [
  {
    code: '66984',
    description: 'Extracapsular cataract removal with insertion of IOL, one stage',
    globalPeriodDays: 90,
  },
  {
    code: '66982',
    description: 'Complex cataract extraction with IOL insertion',
    globalPeriodDays: 90,
  },
] as const;

export const OPHTHALMOLOGY_CATARACT_CPT_CODE_VALUES = OPHTHALMOLOGY_CATARACT_CPT_CODES.map(
  (c) => c.code,
) as [string, ...string[]];

export function findOphthalmologyCataractCptCode(
  code: string,
): OphthalmologyCataractCptCode | undefined {
  return OPHTHALMOLOGY_CATARACT_CPT_CODES.find((entry) => entry.code === code);
}
